import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { trackEvent } from "../Analytics";
import { fmtSlot } from "./SlotPicker";
import { HEARD_ABOUT, getVisitSource } from "../../lib/visitSource";
import { WhatToBring } from "./WhatToBring";
import { FormPrivacyNote } from "../FormPrivacyNote";

const Field = ({ label, hint, testId, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.16em] text-ink-muted">{label}</span>
    <input {...props} data-testid={testId} className="mt-1 w-full rounded-sm border border-stone-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-bronze-dark read-only:bg-stone-100 read-only:text-ink-muted read-only:cursor-not-allowed" />
    {hint && <span className="mt-1 block text-xs text-ink-muted">{hint}</span>}
  </label>
);

const ReturningNote = () => (
  <p data-testid="booking-returning-note" className="text-xs text-ink-muted p-3 bg-cream-100 border border-bronze-light/40 rounded-sm">
    This is a <b>new consultation</b>. Already been seen at the clinic? <Link to="/follow-up" data-testid="booking-followup-link" className="text-bronze-dark underline underline-offset-4">Log in to book a follow-up</Link>.
  </p>
);

const SlotSummary = ({ slot, plan }) => (
  <div>
    <div className="eyebrow">Your appointment</div>
    <div data-testid="booking-form-slot" className="mt-1 font-serif text-2xl">{fmtSlot(slot)}</div>
    {plan.tests.length > 0 && !plan.testsOnly && <div className="text-xs text-ink-muted">Please arrive at this time - your tests come first with our nurse or echo physiologist, then your consultation.</div>}
    <ul data-testid="booking-form-items" className="mt-3 text-sm divide-y divide-stone-200/70 border-y border-stone-200/70">
      {plan.lines.map((l) => <li key={l.label} className="py-1.5">{l.label}</li>)}
    </ul>
    <div data-testid="booking-fee-note" className="mt-2 text-xs text-ink-muted">The full price is shown on the secure payment page before you pay.</div>
  </div>
);

const HeardSelect = ({ value, onChange }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.16em] text-ink-muted">How did you hear about us?</span>
    <select required value={value} onChange={onChange} data-testid="booking-heard-select"
      className="mt-1 w-full rounded-sm border border-stone-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-bronze-dark">
      <option value="" disabled>Please choose…</option>
      {HEARD_ABOUT.map((h) => <option key={h} value={h}>{h}</option>)}
    </select>
  </label>
);

const initialForm = (prefill) => ({
  name: prefill?.name || "", email: prefill?.email || "", phone: prefill?.phone || "", nhs: "",
  heard: prefill ? "I've been here before" : "",
});

const checkoutPayload = ({ form, plan, consultantSlug, slot, reasons }) => ({
  lookup_key: plan.lookupKey, origin_url: window.location.origin,
  consultant_slug: consultantSlug || null, start_time: slot, reasons, holter_days: plan.holterDays || 1,
  invitee_name: form.name, invitee_email: form.email, invitee_phone: form.phone, nhs_number: form.nhs || null,
  heard_about: form.heard, visit_source: getVisitSource(),
});

export const BookingForm = ({ slot, consultantSlug, reasons = [], onSlotTaken, prefill, plan, followUpOnly = false }) => {
  const [form, setForm] = useState(() => initialForm(prefill));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!reasons.length) { setError("Please tick what you'd like to see us about (step 1)."); return; }
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/payments/checkout", checkoutPayload({ form, plan, consultantSlug, slot, reasons }));
      trackEvent("checkout_started", { consultant: consultantSlug || "any" });
      window.location.href = data.checkout_url;
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (err.response?.status === 409) { toast.error(msg); onSlotTaken(); } else setError(msg);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="booking-form" className="bg-white border border-bronze-light/30 rounded-md p-6 space-y-4">
      <SlotSummary slot={slot} plan={plan} />
      {!followUpOnly && !plan.testsOnly && <ReturningNote />}
      <Field label="Full name" required minLength={2} autoComplete="name" value={form.name} onChange={set("name")} testId="booking-name-input" />
      <Field label="Email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} testId="booking-email-input"
        readOnly={followUpOnly} hint={followUpOnly ? "Your account email - your confirmation goes here." : undefined} />
      <Field label="Phone" type="tel" required autoComplete="tel" value={form.phone} onChange={set("phone")} testId="booking-phone-input" />
      <Field label="NHS number (if you have one)" inputMode="numeric" value={form.nhs} onChange={set("nhs")} testId="booking-nhs-input"
        hint="10 digits - found on NHS letters or the NHS App. Kept confidential." />
      <HeardSelect value={form.heard} onChange={set("heard")} />
      {error && <div data-testid="booking-form-error" className="text-sm text-destructive">{error}</div>}
      <button type="submit" disabled={busy} data-testid="booking-pay-btn" className="btn-primary w-full justify-center disabled:opacity-60">
        {busy ? "Checking the time is still free…" : "Continue to secure payment"}
      </button>
      <p className="text-xs text-ink-muted">Nothing is booked until payment succeeds. Your confirmation and calendar invite follow straight after.</p>
      <FormPrivacyNote />
      <WhatToBring testId="booking-form-what-to-bring" />
    </form>
  );
};
